(function() {
  'use strict';

  const STORAGE_KEY = 'recently_viewed_products';
  const CACHE_KEY = 'recently_viewed_cache';
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  const CARD_WIDTH = 200;
  const CARD_GAP = 16;
  const SCROLL_AMOUNT = CARD_WIDTH + CARD_GAP;
  const FADE_OUT_DURATION = 300;

  // Store event listeners for cleanup
  const eventListeners = [];

  function addEventListenerWithCleanup(element, event, handler) {
    if (!element) return;
    element.addEventListener(event, handler);
    eventListeners.push({ element, event, handler });
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function getCachedProduct(handle) {
    try {
      const cache = JSON.parse(window.localStorage.getItem(CACHE_KEY) || '{}');
      const item = cache[handle];
      if (item && Date.now() - item.timestamp < CACHE_DURATION) {
        return item.data;
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  function setCachedProduct(handle, data) {
    try {
      const cache = JSON.parse(window.localStorage.getItem(CACHE_KEY) || '{}');
      cache[handle] = {
        data: data,
        timestamp: Date.now()
      };
      // Clean up old cache entries
      const now = Date.now();
      Object.keys(cache).forEach(function(key) {
        if (now - cache[key].timestamp > CACHE_DURATION) {
          delete cache[key];
        }
      });
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      // Ignore quota errors
    }
  }

  function getHandlesFromStorage() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];

      // Migrate old format (array of strings) to new format (array of objects)
      return arr.map(function(item) {
        if (typeof item === 'string') {
          return { handle: item, timestamp: Date.now() };
        }
        return item;
      });
    } catch (e) {
      return [];
    }
  }

  function saveHandles(products, max) {
    if (!Array.isArray(products)) return;
    const limited = typeof max === 'number' && max > 0 ? products.slice(0, max) : products;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
    } catch (e) {
      // Ignore quota errors
    }
  }

  function trackCurrentProduct() {
    const section = document.querySelector('.recently-viewed-slider-section');
    if (!section) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] No section found');
      return;
    }

    const handle = section.getAttribute('data-current-product-handle');
    if (!handle) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] No product handle found (not on PDP)');
      return;
    }

    if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Tracking product:', handle);

    const max = parseInt(section.getAttribute('data-products-to-show') || '8', 10);
    const currentProducts = getHandlesFromStorage();
    if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Current products in localStorage:', currentProducts);

    // Remove existing product if it exists (to re-add it at the top with new timestamp)
    const filteredProducts = currentProducts.filter(function(p) {
      return p.handle !== handle;
    });

    // Add current product at the beginning with current timestamp
    const now = Date.now();
    filteredProducts.unshift({ handle: handle, timestamp: now });

    saveHandles(filteredProducts, max);

    const savedProducts = getHandlesFromStorage();
    if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Updated products in localStorage:', savedProducts);
  }

  // Format price from cents to dollars
  function formatPrice(cents) {
    if (!cents) return '0.00';
    return (cents / 100).toFixed(2);
  }

  // Calculate time ago string (e.g., "10 minutes ago", "2 hours ago")
  function getTimeAgo(timestamp) {
    if (!timestamp) return 'just now';

    const now = Date.now();
    const diff = now - timestamp; // difference in milliseconds
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) {
      return seconds <= 1 ? '1 second ago' : seconds + ' seconds ago';
    } else if (minutes < 60) {
      return minutes === 1 ? '1 minute ago' : minutes + ' minutes ago';
    } else if (hours < 24) {
      return hours === 1 ? '1 hour ago' : hours + ' hours ago';
    } else if (days < 7) {
      return days === 1 ? '1 day ago' : days + ' days ago';
    } else {
      const weeks = Math.floor(days / 7);
      return weeks === 1 ? '1 week ago' : weeks + ' weeks ago';
    }
  }

  // Create product card HTML from product JSON data
  function createProductCard(productData, showTimestamp, sectionId, index, timestamp) {
    const handle = productData.handle;
    const url = productData.url;
    const title = productData.title;
    const featuredImage = productData.featured_image;
    const variants = productData.variants || [];
    const firstVariant = variants[0] || {};
    const price = formatPrice(firstVariant.price);
    const compareAtPrice = firstVariant.compare_at_price ? formatPrice(firstVariant.compare_at_price) : null;

    // Use variant title only if it's different from product title and not "Default Title"
    let variantTitle = firstVariant.title;
    if (!variantTitle || variantTitle === 'Default Title' || variantTitle === title) {
      variantTitle = title || handle;
    }

    // Calculate time ago
    let timeAgo = getTimeAgo(timestamp);

    let imageHtml = '';
    if (featuredImage) {
      const imageUrl = featuredImage;
      const imageAlt = productData.title || '';

      const imageSizes = [158, 316, 474];
      const srcset = imageSizes.map(size =>
        imageUrl.replace(/(\.[^.]*)(\?.*)?$/, `_${size}x${size}$1$2`) + ` ${size}w`
      ).join(', ');

      imageHtml = `<img srcset="${srcset}" src="${imageUrl}" sizes="(max-width: 749px) 160px, 200px" alt="${imageAlt.replace(/"/g, '&quot;')}" loading="lazy" width="158" height="158" class="card-product-technical__image">`;
    } else {
      imageHtml = '<svg class="card-product-technical__image placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><rect width="525.5" height="525.5" fill="#e5e5e5"/></svg>';
    }

    let priceHtml = '';
    if (compareAtPrice && parseFloat(compareAtPrice) > parseFloat(price)) {
      priceHtml = '<span class="price-item price-item--sale">$' + price + '</span><span class="price-item price-item--regular"><s>$' + compareAtPrice + '</s></span>';
    } else {
      priceHtml = '<span class="price-item price-item--regular">$' + price + '</span>';
    }

    return '<li id="Slide-' + sectionId + '-' + index + '" class="slider__slide">' +
      '<product-card class="card-product-technical-wrapper" data-product-handle="' + handle + '">' +
        '<div class="card-product-technical">' +
          '<div class="card-product-technical__content">' +
            '<div class="card-product--top">' +
              timeAgo +
              '<button type="button" class="recently-close" aria-label="Close">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">' +
                  '<path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
            '<div class="card-product-technical__media">' +
              '<a href="' + url + '" class="card-product-technical__link">' +
                imageHtml +
              '</a>' +
            '</div>' +
            '<div class="card-product-technical__info">' +
              '<a href="' + url + '" class="card-product-technical__code">' +
                variantTitle.toUpperCase() +
              '</a>' +
              '<div class="card-product-technical__price">' +
                priceHtml +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</product-card>' +
    '</li>';
  }

  // Fetch product data from Shopify API
  async function fetchProductData(handle) {
    const cached = getCachedProduct(handle);
    if (cached) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Returning cached product:', handle);
      return cached;
    }

    try {
      const response = await fetch('/products/' + handle + '.js');
      if (!response.ok) {
        throw new Error('HTTP ' + response.status + ': ' + response.statusText);
      }
      const data = await response.json();
      setCachedProduct(handle, data);
      return data;
    } catch (error) {
      console.error('[Recently Viewed] Error fetching product:', handle, error);
      // Return fallback product data or null
      return null;
    }
  }

  async function initSlider() {
    const sliderContainer = document.querySelector('.recently-viewed-slider');
    if (!sliderContainer) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Slider container not found');
      return;
    }

    const sliderList = sliderContainer.querySelector('ul[id^="Slider-"]');
    if (!sliderList) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Slider list not found');
      return;
    }

    const section = sliderContainer.closest('.recently-viewed-slider-section');

    // Accessibility: Add skip link
    const skipLink = document.createElement('a');
    skipLink.href = '#main-content'; // Assuming main content has this ID, or typical Shopify structure
    skipLink.textContent = 'Skip recently viewed products';
    skipLink.className = 'skip-link';
    // Style it to be visually hidden but focusable if not already handled by CSS
    skipLink.style.cssText = 'position: absolute; left: -9999px; z-index: 100; background: #fff; padding: 10px; color: #000;';
    skipLink.onfocus = function() { this.style.left = '0'; this.style.top = '0'; };
    skipLink.onblur = function() { this.style.left = '-9999px'; };
    section.insertBefore(skipLink, section.firstChild);

    const max = section ? parseInt(section.getAttribute('data-products-to-show') || '8', 10) : 8;
    const showTimestamp = section && section.hasAttribute('data-show-timestamp');
    const sectionId = sliderList.id.replace('Slider-', '');
    const viewedProducts = getHandlesFromStorage();

    if (window.SHOPIFY_DEV_MODE) {
      console.log('[Recently Viewed] Initializing slider with products:', viewedProducts);
      console.log('[Recently Viewed] Max products to show:', max);
    }

    if (!viewedProducts.length) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] No products in localStorage, hiding section');
      const wrapper = sliderContainer.closest('.recently-viewed--all-father');
      if (wrapper) wrapper.style.display = 'none';
      return;
    }

    // Clear the list - we'll populate it with fetched products
    sliderList.innerHTML = '';

    // Accessibility: Dynamic content loading
    sliderList.setAttribute('aria-live', 'polite');
    sliderList.setAttribute('aria-busy', 'true');

    // Show loading state if needed
    sliderContainer.classList.add('loading');

    // Fetch products in parallel using Promise.all
    const productPromises = viewedProducts.slice(0, max).map(async function(productItem) {
      const handle = productItem.handle || productItem; // Support both old and new format
      const timestamp = productItem.timestamp || Date.now(); // Use stored timestamp or current time as fallback

      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Fetching product:', handle, 'viewed at:', new Date(timestamp));

      const productData = await fetchProductData(handle);
      return { productData: productData, timestamp: timestamp, handle: handle };
    });

    const results = await Promise.all(productPromises);

    let loadedCount = 0;
    results.forEach(function(result) {
      if (result.productData) {
        const cardHtml = createProductCard(result.productData, showTimestamp, sectionId, loadedCount + 1, result.timestamp);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cardHtml;
        const cardElement = tempDiv.firstElementChild;
        sliderList.appendChild(cardElement);
        loadedCount++;
        if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Added product:', result.handle, 'time ago:', getTimeAgo(result.timestamp));
      }
    });

    // Generate JSON-LD Schema
    updateSchema(results, max);

    if (!sliderList.children.length) {
      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] No products loaded, hiding section');
      const wrapperEmpty = sliderContainer.closest('.recently-viewed--all-father');
      if (wrapperEmpty) wrapperEmpty.style.display = 'none';
    }
    sliderContainer.classList.remove('loading');
    sliderContainer.classList.add('slider-loaded');
    sliderList.setAttribute('aria-busy', 'false');

    // Initialize slider navigation and close buttons
    initSliderNavigation(sliderList);
    initCloseButtons();

    // Wait for images to load
    const images = sliderList.querySelectorAll('img');
    const totalImages = images.length;
    let loaded = 0;

    function checkAllLoaded() {
      loaded++;
      if (loaded >= totalImages || totalImages === 0) {
        if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] All images loaded');
      }
    }

    if (totalImages === 0) {
      checkAllLoaded();
    } else {
      images.forEach(function(img) {
        if (img.complete) {
          checkAllLoaded();
        } else {
          img.addEventListener('load', checkAllLoaded);
          img.addEventListener('error', checkAllLoaded);
        }
      });
    }
  }

  function initSliderNavigation(sliderList) {
    const sliderComponent = sliderList.closest('slider-component');
    if (!sliderComponent) return;

    const prevButton = sliderComponent.querySelector('button[name="previous"]');
    const nextButton = sliderComponent.querySelector('button[name="next"]');

    if (!prevButton || !nextButton) return;

    function scrollSlider(direction) {
      const currentScroll = sliderList.scrollLeft;
      const newScroll = direction === 'next'
        ? currentScroll + SCROLL_AMOUNT
        : currentScroll - SCROLL_AMOUNT;

      sliderList.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }

    addEventListenerWithCleanup(prevButton, 'click', function(e) {
      e.preventDefault();
      scrollSlider('prev');
    });

    addEventListenerWithCleanup(nextButton, 'click', function(e) {
      e.preventDefault();
      scrollSlider('next');
    });

    // Add keyboard navigation
    sliderList.setAttribute('tabindex', '0');
    addEventListenerWithCleanup(sliderList, 'keydown', function(e) {
      if (e.key === 'ArrowLeft') scrollSlider('prev');
      if (e.key === 'ArrowRight') scrollSlider('next');
      if (e.key === 'Home') sliderList.scrollTo({ left: 0, behavior: 'smooth' });
      if (e.key === 'End') sliderList.scrollTo({ left: sliderList.scrollWidth, behavior: 'smooth' });
    });

    function checkScrollPosition() {
      if (!prevButton || !nextButton) return;
      const isStart = sliderList.scrollLeft <= 0;
      const isEnd = Math.abs(sliderList.scrollWidth - sliderList.clientWidth - sliderList.scrollLeft) < 5;

      prevButton.disabled = isStart;
      nextButton.disabled = isEnd;
    }

    // Use for scroll events
    const debouncedScroll = debounce(checkScrollPosition, 100);
    addEventListenerWithCleanup(sliderList, 'scroll', debouncedScroll);

    // Initial check
    checkScrollPosition();
  }

  function initCloseButtons() {
    const closeButtons = document.querySelectorAll('.recently-close');

    closeButtons.forEach(function(button) {
      if (button.dataset.rvBound === 'true') return;
      button.dataset.rvBound = 'true';

      addEventListenerWithCleanup(button, 'click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const cardWrapper = button.closest('product-card');
        if (!cardWrapper) return;

        const handle = cardWrapper.closest('.card-product-technical-wrapper').getAttribute('data-product-handle');
        const slideElement = cardWrapper.closest('li.slider__slide');
        if (!slideElement) return;

        if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Removing product:', handle);

        if (handle) {
          const products = getHandlesFromStorage();
          const filtered = products.filter(function(p) {
            const productHandle = p.handle || p; // Support both old and new format
            return productHandle !== handle;
          });
          saveHandles(filtered, 20);
          if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Updated localStorage after removal:', getHandlesFromStorage());
        }

        // Capture next card for focus management before removal
        const nextCard = slideElement.nextElementSibling;

        cardWrapper.classList.add('fade-out');

        setTimeout(function() {
          if (slideElement.parentElement) {
            slideElement.remove();
          }

          // Restore focus
          if (nextCard) {
            const nextButton = nextCard.querySelector('.recently-close');
            if (nextButton) nextButton.focus();
          } else {
            const clearButton = document.querySelector('[data-clear-recently-viewed]');
            if (clearButton) clearButton.focus();
          }

          const sliderList = slideElement.closest('ul[id^="Slider-"]');
          if (sliderList && !sliderList.children.length) {
            const wrapper = sliderList.closest('.recently-viewed--all-father');
            if (wrapper) wrapper.style.display = 'none';
            if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] No more products, hiding section');
          }
        }, FADE_OUT_DURATION);
      });
    });
  }

  function initClearButton() {
    const clearButton = document.querySelector('[data-clear-recently-viewed]');
    if (!clearButton) return;

    addEventListenerWithCleanup(clearButton, 'click', function(e) {
      e.preventDefault();

      // Clear localStorage
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Error clearing localStorage');
      }

      // Clear list from DOM
      const sliderList = document.querySelector('ul[id^="Slider-"]');
      if (sliderList) {
        sliderList.innerHTML = '';
      }

      // Hide section immediately
      const section = document.querySelector('.recently-viewed--all-father');
      if (section) {
        section.style.display = 'none';
      }

      if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] History cleared and section hidden');
    });
  }

  function updateSchema(results, max) {
    const section = document.querySelector('.recently-viewed-slider-section');
    if (!section) return;

    const titleElement = section.querySelector('.recently-viewed-slider__title');
    const title = titleElement ? titleElement.textContent.trim() : 'Recently Viewed';

    const validProducts = results
      .filter(r => r.productData)
      .slice(0, max);

    if (validProducts.length === 0) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": title,
      "itemListElement": validProducts.map((item, index) => ({
        "@type": "Product",
        "position": index + 1,
        "name": item.productData.title,
        "url": window.location.origin + item.productData.url,
        "image": item.productData.featured_image,
        "offers": {
          "@type": "Offer",
          "price": (item.productData.variants[0].price / 100).toFixed(2),
          "priceCurrency": window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD'
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  }

  function onReady() {
    if (window.SHOPIFY_DEV_MODE) console.log('[Recently Viewed] Initializing...');
    trackCurrentProduct();
    initClearButton();

    setTimeout(function() {
      initSlider();
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
  });
})();
